import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { fetchAuthSession } from '@aws-amplify/auth';

import classes from './StripeReturn.module.css';
import { trackPayoutSetupReturned } from '../../util/tracking';
import {
  GET_USER_STRIPE_STATUS,
  CREATE_ONBOARDING_LINK,
  STRIPE_WEB_RETURN_URL,
  STRIPE_WEB_REFRESH_URL,
} from '../../graphql/queries/financeQueries';

const StripeReturn = () => {
  const navigate = useNavigate();
  const client = useApolloClient();

  // stripe_refresh=true => der Onboarding-Link ist abgelaufen und muss erneuert werden.
  const [isRefresh] = useState(
    () => new URLSearchParams(window.location.search).get('stripe_refresh') === 'true'
  );

  useEffect(() => {
    let cancelled = false;

    trackPayoutSetupReturned({
      source: isRefresh ? 'stripe_refresh_page' : 'stripe_return_page',
    });

    // Fallback: zurück ins Profil, dort kann der User über "Fortsetzen" neu starten.
    const goToProfile = () => {
      if (cancelled) return;
      navigate('/profile', {
        replace: true,
        state: { activeSection: 'auszahlung', stripeReturn: true },
      });
    };

    // Normaler Rücksprung: kurz anzeigen, dann zurück ins Profil (Status wird dort refetcht).
    if (!isRefresh) {
      const timer = setTimeout(goToProfile, 1500);
      return () => {
        cancelled = true;
        clearTimeout(timer);
      };
    }

    // Refresh: frischen Onboarding-Link holen und im SELBEN Tab zurück zu Stripe leiten.
    (async () => {
      try {
        const session = await fetchAuthSession();
        const userId = session.tokens?.idToken?.payload?.sub;
        if (!userId) {
          goToProfile();
          return;
        }

        const { data: statusData } = await client.query({
          query: GET_USER_STRIPE_STATUS,
          variables: { id: userId },
          fetchPolicy: 'network-only',
        });
        const accountId = statusData?.getUser?.stripeConnectAccountId;
        if (!accountId) {
          goToProfile();
          return;
        }

        const { data: linkData } = await client.mutate({
          mutation: CREATE_ONBOARDING_LINK,
          variables: {
            accountId,
            returnUrl: STRIPE_WEB_RETURN_URL,
            refreshUrl: STRIPE_WEB_REFRESH_URL,
          },
        });

        const url = linkData?.createOnboardingLink?.url;
        if (url && !cancelled) {
          window.location.replace(url);
        } else {
          goToProfile();
        }
      } catch (err) {
        goToProfile();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isRefresh, navigate, client]);

  return (
    <div className={classes.page}>
      <div className={classes.card}>
        <div className={classes.icon}>💳</div>
        <h1 className={classes.title}>
          {isRefresh ? 'Stripe-Sitzung wird erneuert …' : 'Stripe Einrichtung wird abgeschlossen …'}
        </h1>
        <p className={classes.text}>
          {isRefresh
            ? 'Dein Stripe-Link ist abgelaufen. Wir leiten dich gleich automatisch zurück zu Stripe, um die Einrichtung fortzusetzen.'
            : 'Du wirst gleich zurück zu deinem Profil weitergeleitet. Dort kannst du den aktuellen Auszahlungsstatus einsehen.'}
        </p>
      </div>
    </div>
  );
};

export default StripeReturn;
